import { Box, Typography, Chip, Avatar } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { styled } from "@mui/system";
import { useTranslation } from "react-i18next";

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 60,
  height: 60,
  margin: "0 auto",
  border: `2px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
}));

function UserDetailsPanel({ userDetails }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const getInitials = (firstName, lastName) => {
    if (!firstName || !lastName) return t("myUserManagement.naText");
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  };

  return (
    <Box
      sx={{
        p: 3,
        backgroundColor: alpha(theme.palette.primary.main, 0.02),
        borderRadius: 2,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        m: 1,
        boxShadow: theme.shadows[1],
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: theme.palette.text.primary,
          mb: 3,
        }}
      >
        {t("myUserManagement.userDetailsTitle")}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 3,
          p: 2,
          bgcolor: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        <StyledAvatar
          src={userDetails?.avatar?.url || ""}
          alt={userDetails?.username || t("myUserManagement.naText")}
        >
          {!userDetails?.avatar?.url &&
            getInitials(userDetails?.first_name, userDetails?.last_name)}
        </StyledAvatar>
        <Box sx={{ ml: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
            {userDetails?.first_name || t("myUserManagement.naText")}{" "}
            {userDetails?.last_name || t("myUserManagement.naText")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500 }}
          >
            @{userDetails?.username || t("myUserManagement.naText")}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gap: 3,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: theme.palette.primary.main,
            }}
          >
            {t("myUserManagement.authorizedSitesLabel")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {userDetails?.sites?.map((site) => (
              <Chip
                key={site.id}
                label={site.name}
                color="info"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )) || (
              <Typography color="text.secondary">
                {t("myUserManagement.naText")}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: theme.palette.primary.main,
            }}
          >
            {t("myUserManagement.userGroupsLabel")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {userDetails?.user_groups?.map((group) => (
              <Chip
                key={group.id}
                label={group.name}
                color="primary"
                size="small"
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            )) || (
              <Typography color="text.secondary">
                {t("myUserManagement.naText")}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette.primary.main,
            }}
          >
            {t("myUserManagement.departmentLabel")}
          </Typography>
          <Typography sx={{ fontWeight: 500 }}>
            {userDetails?.department?.name || t("myUserManagement.naText")}
          </Typography>
        </Box>

        <Box
          sx={{
            p: 2,
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              mb: 1,
              color: theme.palette.primary.main,
            }}
          >
            {t("myUserManagement.managerLabel")}
          </Typography>
          <Typography sx={{ fontWeight: 500 }}>
            {userDetails?.manager
              ? `${userDetails.manager.first_name} ${userDetails.manager.last_name || t("myUserManagement.naText")}`
              : t("myUserManagement.noManagerText")}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default UserDetailsPanel;
